import type { CustomRoleTaxonomySelection } from '@revelio/data-access';

import type { TreeSelection } from '../../next/CustomDash/filters/types/filters';
import type { GeographyTaxonomyPreference } from './utils/geography-preference';
import type { WorkforceSegment } from './utils/workforce-segment';

/**
 * Color theme preference. 'system' follows the OS `prefers-color-scheme`;
 * 'light'/'dark' force the respective theme.
 *
 * `system` is the default. Rows carrying the retired 'legacy' member may
 * still exist in dash-meta, so `preferences[ColorMode]` can still hold that
 * string at runtime even though the type no longer admits it — every read of
 * the stored value must go through `isColorModePreference` and fall back to
 * the default rather than trusting the declared type.
 */
const COLOR_MODE_PREFERENCES = ['system', 'light', 'dark'] as const;

export type ColorModePreference = (typeof COLOR_MODE_PREFERENCES)[number];

export function isColorModePreference(
  value: unknown
): value is ColorModePreference {
  return COLOR_MODE_PREFERENCES.includes(value as ColorModePreference);
}

/**
 * Which industry list the Economic Impact scenario form offers. `rics` (the
 * default) is Revelio's own industry taxonomy — the MRIO API expands each pick
 * into the BEA industries it is built on through its taxonomy bridge. `bea`
 * lists those BEA input-output industries directly, for anyone who wants the
 * model's native grain.
 */
export const ECONOMIC_IMPACT_INDUSTRY_CLASSIFICATIONS = [
  'rics',
  'bea',
] as const;

export type EconomicImpactIndustryClassification =
  (typeof ECONOMIC_IMPACT_INDUSTRY_CLASSIFICATIONS)[number];

/** Each list's name, in the preferences dialog and the industry picker alike. */
export const ECONOMIC_IMPACT_INDUSTRY_CLASSIFICATION_LABEL: Record<
  EconomicImpactIndustryClassification,
  string
> = {
  rics: 'Revelio Industry Classification (RICS)',
  bea: 'BEA industries',
};

export const DEFAULT_ECONOMIC_IMPACT_INDUSTRY_CLASSIFICATION: EconomicImpactIndustryClassification =
  'bea';

export function isEconomicImpactIndustryClassification(
  value: unknown
): value is EconomicImpactIndustryClassification {
  return ECONOMIC_IMPACT_INDUSTRY_CLASSIFICATIONS.includes(
    value as EconomicImpactIndustryClassification
  );
}

/**
 * The stored value is untrusted at runtime (a hand-edited row, or one written
 * by a build that knew other members), so every read falls back to the
 * default rather than trusting the declared type.
 */
export function resolveEconomicImpactIndustryClassification(
  value: unknown
): EconomicImpactIndustryClassification {
  return isEconomicImpactIndustryClassification(value)
    ? value
    : DEFAULT_ECONOMIC_IMPACT_INDUSTRY_CLASSIFICATION;
}

/**
 * Which geographies the Economic Impact page places investments in and
 * reports on. `county` (the default) is the MRIO model's own grain: US
 * counties, grouped by state. `city` is Revelio's city taxonomy, grouped by
 * metro area as across the rest of the dashboard — the API's location bridge
 * spreads a city's investment over the counties it spans and reports the
 * ripple by city, plus the rest of each county outside one.
 */
const ECONOMIC_IMPACT_GEOGRAPHIES = ['county', 'city'] as const;

export type EconomicImpactGeography =
  (typeof ECONOMIC_IMPACT_GEOGRAPHIES)[number];

export const DEFAULT_ECONOMIC_IMPACT_GEOGRAPHY: EconomicImpactGeography =
  'county';

export function isEconomicImpactGeography(
  value: unknown
): value is EconomicImpactGeography {
  return ECONOMIC_IMPACT_GEOGRAPHIES.includes(value as EconomicImpactGeography);
}

/** Untrusted at runtime like the industry classification; see above. */
export function resolveEconomicImpactGeography(
  value: unknown
): EconomicImpactGeography {
  return isEconomicImpactGeography(value)
    ? value
    : DEFAULT_ECONOMIC_IMPACT_GEOGRAPHY;
}

// Duplicated values to avoid circular dependency with filter types
export const UserPreferenceControlledFilterIds: ReadonlyArray<string> = [
  'CONTINGENT_WORKERS',
  'DATA_ADJUSTMENTS',
  'ROLE_TAXONOMY',
  'SKILL_TAXONOMY',
  'ACTIVITY_TAXONOMY',
  'GEOGRAPHY_TAXONOMY',
];

export enum PreferenceScope {
  GLOBAL = 'GLOBAL',
  INDIVIDUAL = 'INDIVIDUAL',
  COMPANY = 'COMPANY',
  INDUSTRY = 'INDUSTRY',
  GEOGRAPHY = 'GEOGRAPHY',
  ROLE = 'ROLE',
  SKILL = 'SKILL',
  ACTIVITY = 'ACTIVITY',
}

export enum UserPreferenceKey {
  ContingentWorkers = 'contingentWorkers',
  Taxonomies = 'taxonomies',
  DataAdjustments = 'dataAdjustments',
  DefaultFilters = 'defaultFilters',
  Currency = 'currency',
  Geography = 'geography',
  PageOrder = 'pageOrder',
  ColorMode = 'colorMode',
  EconomicImpactIndustries = 'economicImpactIndustries',
  EconomicImpactGeography = 'economicImpactGeography',
}

export enum UserPreferenceSubKey {
  RoleTaxonomy = 'roleTaxonomy',
  SkillTaxonomy = 'skillTaxonomy',
  ActivityTaxonomy = 'activityTaxonomy',
}

export type UserPreferences = {
  [UserPreferenceKey.ContingentWorkers]: boolean | WorkforceSegment;
  [UserPreferenceKey.Taxonomies]: {
    role?: 'adaptive' | CustomRoleTaxonomySelection;
    effectiveRoleTaxonomySelection?: CustomRoleTaxonomySelection | null;
    /** Role taxonomy version the saved DefaultFilters were captured under. */
    roleTaxonomyVersion?: string;
    skill?: 'adaptive' | number;
    effectiveSkillTaxonomySelection?: number | null;
    /** Stored for symmetry with role; no consumer yet. */
    skillTaxonomyVersion?: string;
    activity?: 'adaptive' | number;
    effectiveActivityTaxonomySelection?: number | null;
    activityTaxonomyVersion?: string;
  };
  /**
   * Whether to use raw (unadjusted) data.
   * false => Adjusted (default)
   * true  => Unadjusted (raw)
   */
  [UserPreferenceKey.DataAdjustments]: boolean;
  [UserPreferenceKey.Currency]: {
    id?: string;
    code: string;
    rate: number;
  };
  [UserPreferenceKey.DefaultFilters]: TreeSelection[];
  [UserPreferenceKey.Geography]: GeographyTaxonomyPreference;
  [UserPreferenceKey.PageOrder]: string[];
  [UserPreferenceKey.ColorMode]: ColorModePreference;
  [UserPreferenceKey.EconomicImpactIndustries]: EconomicImpactIndustryClassification;
  [UserPreferenceKey.EconomicImpactGeography]: EconomicImpactGeography;
};

/** List of keys that are considered global-only */
export const GLOBAL_PREFERENCE_KEYS: (keyof UserPreferences)[] = [
  UserPreferenceKey.Currency,
  UserPreferenceKey.Geography,
  UserPreferenceKey.ColorMode,
  UserPreferenceKey.Taxonomies,
  UserPreferenceKey.DataAdjustments,
  UserPreferenceKey.ContingentWorkers,
  UserPreferenceKey.EconomicImpactIndustries,
  UserPreferenceKey.EconomicImpactGeography,
];

/**
 * Preferences that manage their own persistence (immediate write, own reset
 * UX) and should NOT participate in the modal's draft/diff/bulk-restore flow.
 * Excluded from `calculateVisiblePreferenceDiff`, `saveVisiblePreferencesAsDefault`,
 * and `restoreVisiblePreferencesToDefaults`.
 */
export const SELF_MANAGED_PREFERENCE_KEYS: UserPreferenceKey[] = [
  UserPreferenceKey.PageOrder,
];

/** Scopes where `PageOrder` is editable / cleaned-up. */
export const PAGE_ORDER_SCOPES: PreferenceScope[] = [
  PreferenceScope.COMPANY,
  PreferenceScope.INDUSTRY,
  PreferenceScope.GEOGRAPHY,
  PreferenceScope.ROLE,
  PreferenceScope.SKILL,
  PreferenceScope.ACTIVITY,
];

/**
 * Keys whose value is specific to a primary-filter scope rather than global.
 * These render below the divider + scope switcher in the preferences rail and
 * save to the switcher's selected scope; everything else is a global choice.
 */
export const SCOPED_PREFERENCE_KEYS: UserPreferenceKey[] = [
  UserPreferenceKey.DefaultFilters,
  UserPreferenceKey.PageOrder,
];
